import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ROLES, type AuthUser, type RoleName } from '@chronos/shared';
import { apiFetch } from '@/api/client';
import { useUsers } from '@/hooks/queries';
import { Card, ConfirmButton, EmptyState, ErrorNote, Modal, PageHeader, Spinner, SubmitButton } from '@/components/ui';
import { formatDate } from '@/lib/format';

export function UsersPage() {
  const [q, setQ] = useState('');
  const [status, setStatus] = useState('');
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<AuthUser | null>(null);
  const [issued, setIssued] = useState<{ username: string; password: string } | null>(null);

  const { data, isLoading, error } = useUsers({ q: q || undefined, status: status || undefined });
  const qc = useQueryClient();
  const invalidate = () => void qc.invalidateQueries({ queryKey: ['users'] });

  const remove = useMutation({
    mutationFn: (id: string) => apiFetch<void>(`/api/users/${id}`, { method: 'DELETE' }),
    onSuccess: invalidate,
  });

  const resetPassword = useMutation({
    mutationFn: (user: AuthUser) =>
      apiFetch<{ temporaryPassword?: string }>(`/api/users/${user.id}/reset-password`, { method: 'POST', body: {} }).then((r) => ({
        username: user.username,
        password: r.temporaryPassword ?? '',
      })),
    onSuccess: (r) => {
      setIssued(r);
      invalidate();
    },
  });

  return (
    <div className="mx-auto max-w-6xl">
      <PageHeader
        title="Users"
        description="Create accounts, assign roles, and deactivate people who have left."
        actions={<button className="btn-primary" onClick={() => setCreating(true)}>New user</button>}
      />

      <div className="mb-4 flex flex-wrap gap-2">
        <input className="input max-w-xs" placeholder="Search name, email or username" value={q} onChange={(e) => setQ(e.target.value)} />
        <select className="input max-w-[10rem]" value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="">All statuses</option>
          <option value="ACTIVE">Active</option>
          <option value="INACTIVE">Deactivated</option>
        </select>
      </div>

      <ErrorNote error={error ?? remove.error ?? resetPassword.error} />

      <Card className="mt-3 overflow-hidden">
        {isLoading ? (
          <div className="flex justify-center py-16"><Spinner /></div>
        ) : (data?.items.length ?? 0) === 0 ? (
          <EmptyState title="No users match" description="Try a different search, or create a new account." />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="border-b border-slate-200 bg-slate-50">
                <tr>
                  <th className="th">Name</th>
                  <th className="th">Username</th>
                  <th className="th">Email</th>
                  <th className="th">Roles</th>
                  <th className="th">Status</th>
                  <th className="th">Joined</th>
                  <th className="th text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {data?.items.map((u) => (
                  <tr key={u.id} className="hover:bg-slate-50">
                    <td className="td font-medium text-slate-900">
                      {u.name}
                      {u.mustChangePassword && <span className="ml-2 badge bg-amber-100 text-amber-800">password pending</span>}
                    </td>
                    <td className="td font-mono text-xs">{u.username}</td>
                    <td className="td text-slate-500">{u.email}</td>
                    <td className="td">
                      <div className="flex flex-wrap gap-1">
                        {u.roles.map((r) => (
                          <span key={r} className={`badge ${r === 'ADMIN' ? 'bg-brand-100 text-brand-800' : r === 'MANAGER' ? 'bg-violet-100 text-violet-800' : 'bg-slate-100 text-slate-600'}`}>
                            {r.toLowerCase()}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td className="td">
                      <span className={`badge ${u.status === 'ACTIVE' ? 'bg-emerald-100 text-emerald-800' : 'bg-slate-200 text-slate-600'}`}>
                        {u.status === 'ACTIVE' ? 'active' : 'deactivated'}
                      </span>
                    </td>
                    <td className="td text-slate-500">{formatDate(u.createdAt)}</td>
                    <td className="td">
                      <div className="flex justify-end gap-1">
                        <button className="btn-ghost px-2 py-1 text-xs" onClick={() => setEditing(u)}>Edit</button>
                        <button className="btn-ghost px-2 py-1 text-xs" onClick={() => resetPassword.mutate(u)} disabled={resetPassword.isPending}>Reset password</button>
                        <ConfirmButton className="btn-ghost px-2 py-1 text-xs text-rose-600" label="Delete" confirmLabel="Confirm" onConfirm={() => remove.mutate(u.id)} />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {creating && <UserModal onClose={() => setCreating(false)} onIssued={setIssued} />}
      {editing && <UserModal key={editing.id} user={editing} onClose={() => setEditing(null)} onIssued={setIssued} />}

      <Modal open={!!issued} title="Temporary password" onClose={() => setIssued(null)}>
        <p className="text-sm text-slate-600">
          Give this to <span className="font-medium">{issued?.username}</span>. It is shown once and cannot be retrieved later; they must change it at first sign-in.
        </p>
        <p className="mt-3 select-all rounded-lg bg-slate-100 px-3 py-2 font-mono text-sm">{issued?.password}</p>
      </Modal>
    </div>
  );
}

function UserModal({ user, onClose, onIssued }: { user?: AuthUser; onClose: () => void; onIssued: (v: { username: string; password: string }) => void }) {
  const qc = useQueryClient();
  const [form, setForm] = useState({
    name: user?.name ?? '',
    email: user?.email ?? '',
    username: user?.username ?? '',
    status: user?.status ?? 'ACTIVE',
    roles: (user?.roles ?? ['USER']) as RoleName[],
    dateOfBirth: user?.personal?.dateOfBirth ?? '',
    anniversaryDate: user?.personal?.anniversaryDate ?? '',
    phone: user?.personal?.phone ?? '',
    address: user?.personal?.address ?? '',
  });

  // Empty inputs mean "clear it", which the API expresses as null.
  const personal = () => ({
    dateOfBirth: form.dateOfBirth || null,
    anniversaryDate: form.anniversaryDate || null,
    phone: form.phone || null,
    address: form.address || null,
  });

  const save = useMutation({
    mutationFn: async () => {
      if (user) {
        return apiFetch<AuthUser>(`/api/users/${user.id}`, {
          method: 'PATCH',
          body: { name: form.name, email: form.email, username: form.username, status: form.status, roles: form.roles, ...personal() },
        });
      }
      const res = await apiFetch<{ user: AuthUser; temporaryPassword?: string }>('/api/users', {
        method: 'POST',
        body: { name: form.name, email: form.email, username: form.username, roles: form.roles, ...personal() },
      });
      if (res.temporaryPassword) onIssued({ username: res.user.username, password: res.temporaryPassword });
      return res.user;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['users'] });
      onClose();
    },
  });

  const toggleRole = (role: RoleName) =>
    setForm((f) => ({
      ...f,
      // At least one role must remain assigned.
      roles: f.roles.includes(role) ? (f.roles.length > 1 ? f.roles.filter((r) => r !== role) : f.roles) : [...f.roles, role],
    }));

  return (
    <Modal
      open
      title={user ? `Edit ${user.name}` : 'New user'}
      onClose={onClose}
      footer={
        <>
          <button className="btn-secondary" onClick={onClose}>Cancel</button>
          <SubmitButton form="user-form" pending={save.isPending} className="btn-primary">{user ? 'Save changes' : 'Create user'}</SubmitButton>
        </>
      }
    >
      <form id="user-form" className="space-y-4" onSubmit={(e) => { e.preventDefault(); save.mutate(); }}>
        <ErrorNote error={save.error} />
        <div>
          <label className="label" htmlFor="uname">Full name</label>
          <input id="uname" className="input" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <label className="label" htmlFor="uuser">Username</label>
            <input id="uuser" className="input" required minLength={3} pattern="[A-Za-z0-9._\-]+" value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} />
          </div>
          <div>
            <label className="label" htmlFor="uemail">Email</label>
            <input id="uemail" type="email" className="input" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
          </div>
        </div>
        <div>
          <span className="label">Roles</span>
          <div className="flex flex-wrap gap-2">
            {ROLES.map((r) => (
              <button
                type="button"
                key={r}
                onClick={() => toggleRole(r)}
                className={`rounded-lg border px-3 py-1.5 text-sm transition ${form.roles.includes(r) ? 'border-brand-500 bg-brand-50 font-medium text-brand-700' : 'border-slate-300 text-slate-600 hover:bg-slate-50'}`}
              >
                {r.toLowerCase()}
              </button>
            ))}
          </div>
          <p className="mt-1.5 text-xs text-slate-500">
            Managers additionally need to be set as manager on a project to see its team.
          </p>
        </div>
        <fieldset className="space-y-3 rounded-lg border border-slate-200 p-3">
          <legend className="px-1 text-xs font-semibold uppercase tracking-wide text-slate-500">Personal details</legend>
          <p className="text-xs text-slate-500">Visible to admins and to this person only — managers never see these. The employee can also edit these from My profile.</p>
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <label className="label" htmlFor="udob">Date of birth</label>
              <input id="udob" type="date" className="input" value={form.dateOfBirth} onChange={(e) => setForm({ ...form, dateOfBirth: e.target.value })} />
            </div>
            <div>
              <label className="label" htmlFor="uanniv">Anniversary</label>
              <input id="uanniv" type="date" className="input" value={form.anniversaryDate} onChange={(e) => setForm({ ...form, anniversaryDate: e.target.value })} />
            </div>
          </div>
          <div>
            <label className="label" htmlFor="uphone">Phone number</label>
            <input id="uphone" type="tel" className="input" maxLength={40} placeholder="+91 98765 43210" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
          </div>
          <div>
            <label className="label" htmlFor="uaddr">Address</label>
            <textarea id="uaddr" className="input" rows={3} maxLength={500} value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} />
          </div>
        </fieldset>
        {user && (
          <div>
            <label className="label" htmlFor="ustatus">Status</label>
            <select id="ustatus" className="input" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value as 'ACTIVE' | 'INACTIVE' })}>
              <option value="ACTIVE">Active</option>
              <option value="INACTIVE">Deactivated — cannot sign in</option>
            </select>
          </div>
        )}
        {!user && (
          <p className="rounded-lg bg-slate-50 px-3 py-2 text-xs text-slate-600">
            A temporary password is generated and shown once after the account is created.
          </p>
        )}
      </form>
    </Modal>
  );
}
